import {Injectable} from '@angular/core';
import {HttpContext} from '@angular/common/http';
import {ApiClient} from 'src/app/infrastructure/http/api.client';
import {SKIP_GLOBAL_ERROR_REDIRECT} from 'src/app/infrastructure/http/http-context';
import {
  CustomRangeReturn,
  MetricReturn,
  PeriodReturn,
  PortfolioSnapshotSummary,
  SnapshotSummaryPeriodKey
} from 'src/app/domain/model/portfolio-snapshot-summary.model';

@Injectable({
  providedIn: 'root',
})
export default class PortfolioSnapshotSummaryApi {
  private static readonly summaryCacheTtlMs = 5 * 60 * 1000;
  private static readonly periodKeys: SnapshotSummaryPeriodKey[] = ['total', 'lastMonth', 'lastThreeMonths', 'ytd', 'lastTwelveMonths'];

  private readonly url = 'portfolio/snapshot/summary';
  private readonly cache = new Map<string, {summary: PortfolioSnapshotSummary; cachedAt: number}>();
  private readonly pending = new Map<string, Promise<PortfolioSnapshotSummary | null>>();

  constructor(private api: ApiClient) {
  }

  public async fetch(portfolioId: string, from?: string, to?: string): Promise<PortfolioSnapshotSummary | null> {
    const key = this.cacheKey(portfolioId, from, to);
    const cached = this.cached(key);

    if (cached) {
      return cached;
    }

    const pending = this.pending.get(key);

    if (pending) {
      return pending;
    }

    const request = this.request(portfolioId, from, to)
      .then((summary) => {
        this.cache.set(key, {summary, cachedAt: Date.now()});
        return summary;
      })
      .catch(() => null)
      .finally(() => this.pending.delete(key));

    this.pending.set(key, request);

    return request;
  }

  public invalidate(): void {
    this.cache.clear();
    this.pending.clear();
  }

  private async request(portfolioId: string, from?: string, to?: string): Promise<PortfolioSnapshotSummary> {
    const params: Record<string, string> = {portfolio: portfolioId};

    if (from && to) {
      params.from = from;
      params.to = to;
    }

    const response = await this.api.get<any>(this.url, params, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true)
    });

    return this.toSummary(response);
  }

  private toSummary(response: any): PortfolioSnapshotSummary {
    return {
      referenceDate: response?.referenceDate,
      value: response?.value ?? null,
      performanceAmount: response?.performanceAmount ?? null,
      periods: PortfolioSnapshotSummaryApi.periodKeys.reduce(
        (periods, key) => ({...periods, [key]: this.toPeriod(response?.[key])}),
        {} as Record<SnapshotSummaryPeriodKey, PeriodReturn>
      ),
      customRange: this.toCustomRange(response?.customRange)
    };
  }

  private toCustomRange(response: any): CustomRangeReturn | null {
    if (!response) {
      return null;
    }

    return {...this.toPeriod(response.period), fromDate: response.fromDate, toDate: response.toDate};
  }

  private toPeriod(response: any): PeriodReturn {
    return {
      yourPortfolio: this.toMetric(response?.yourPortfolio),
      modelPortfolio: this.toMetric(response?.modelPortfolio),
      benchmark: this.toMetric(response?.benchmark),
      vsBenchmark: response?.vsBenchmark ?? null
    };
  }

  private toMetric(response: any): MetricReturn | null {
    if (!response) {
      return null;
    }

    return {performance: response.performance ?? null, tae: response.tae ?? null};
  }

  private cached(key: string): PortfolioSnapshotSummary | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() - entry.cachedAt > PortfolioSnapshotSummaryApi.summaryCacheTtlMs) {
      this.cache.delete(key);
      return null;
    }

    return entry.summary;
  }

  private cacheKey(portfolioId: string, from?: string, to?: string): string {
    return from && to ? `${portfolioId}:${from}:${to}` : portfolioId;
  }
}
